/**
 * useMenuDismiss — lukker en utfoldbar ⋯-meny (#158, #159).
 *
 * Felles for AssistantMenu og radmenyen på Maler. Menyen er en vanlig knapp
 * med `aria-expanded`, ikke en ARIA-meny, så det eneste den trenger er å
 * lukkes riktig:
 *   - et trykk utenfor lukker uten å flytte fokus;
 *   - Esc lukker, og gir fokus tilbake til knappen når fokus var i menyen;
 *   - fokus som tabber ut av menyen lukker (returverdien går på `onBlur`
 *     til elementet som holder både knappen og panelet).
 */
import { useEffect, type FocusEvent, type RefObject } from 'react';

export function useMenuDismiss(
  open: boolean,
  setOpen: (open: boolean) => void,
  rootRef: RefObject<HTMLElement | null>,
  triggerRef: RefObject<HTMLElement | null>,
): (e: FocusEvent<HTMLElement>) => void {
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      const focusInside = rootRef.current?.contains(document.activeElement) ?? false;
      setOpen(false);
      if (focusInside) triggerRef.current?.focus();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, setOpen, rootRef, triggerRef]);

  // Bare når fokus faktisk flytter til noe annet. Safari fokuserer ikke en
  // knapp ved trykk, så et trykk på et valg i panelet gir blur med
  // `relatedTarget = null`; å lukke da ville fjerne valget før klikket kom fram.
  // Trykk utenfor tas av pointerdown-lytteren over.
  return (e) => {
    const next = e.relatedTarget as Node | null;
    if (open && next && !rootRef.current?.contains(next)) setOpen(false);
  };
}
